/**
 * Google Ads utility — loads gtag dynamically from the tag ID stored in the admin config.
 * The tag ID + conversion label are fetched from GET /api/v1/google-ads/public-config
 * so admins can change them from the dashboard without a frontend redeploy.
 */

import apiClient from '../api/client';
import { setFeedIdPrefix } from './analytics';

// In-memory cache so we don't re-fetch on every page
let _config = null;

export async function loadGoogleAdsConfig() {
  if (_config) return _config;
  try {
    // apiClient supplies the real API base URL and the X-Tenant-ID header
    const res = await apiClient.get('/google-ads/public-config');
    _config = res.data;
    setFeedIdPrefix(_config?.feedIdPrefix);
    return _config;
  } catch {
    return null;
  }
}

/**
 * Initialise gtag.js.  Must be called once, as early as possible (App mount).
 * If tagId is not configured in the admin dashboard this is a no-op.
 */
export async function initGoogleAds() {
  const config = await loadGoogleAdsConfig();
  if (!config?.enabled || !config?.tagId) return;

  // Avoid double-loading if the script tag already exists
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${config.tagId}`;
  document.head.appendChild(script);

  script.onload = () => {
    window.gtag('js', new Date());
    window.gtag('config', config.tagId);
  };
}

/**
 * Fire a Google Ads purchase conversion event.
 * Call this on the order success page.
 *
 * @param {string} transactionId  Order number
 * @param {number} value          Order total
 * @param {string} currency       Currency code (default 'INR')
 */
export async function fireConversionEvent(transactionId, value, currency = 'INR') {
  const config = await loadGoogleAdsConfig();
  if (!config?.enabled || !config?.tagId || !config?.conversionLabel) return;
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'conversion', {
    send_to:        `${config.tagId}/${config.conversionLabel}`,
    value:          value || 0,
    currency,
    transaction_id: transactionId,
  });
}

/**
 * Shopping steps — optional Secondary conversion actions set in Admin → Google Ads → "Shopping steps", so Google Ads shows
 * how many people add to cart / start checkout without buying. Nothing is sent while a step's label is empty.
 */
async function fireShoppingStep(labelKey, value, currency) {
  try {
    const config = await loadGoogleAdsConfig();
    const label = config?.[labelKey];
    if (!config?.enabled || !config?.tagId || !label) return;
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'conversion', {
      send_to:  `${config.tagId}/${label}`,
      value:    Number(value) || 0,
      currency: currency || 'INR',
    });
  } catch {
    // tracking must never break shopping
  }
}

/** A product was added to the cart (value = price × quantity). */
export const fireAddToCartConversion = (value, currency) => fireShoppingStep('addToCartLabel', value, currency);

/** The checkout opened with items in the cart (value = cart value). */
export const fireBeginCheckoutConversion = (value, currency) => fireShoppingStep('beginCheckoutLabel', value, currency);
